import { IRck21ConfigService } from "./IRck21ConfigService";
import { Api } from "lavva.exalushome";
import { ExalusConnectionService } from "lavva.exalushome/build/js/Services/ExalusConnectionService";
import { IExalusConnectionService } from "lavva.exalushome/build/js/Services/IExalusConnectionService";
import { DataFrame, Method, Status } from "lavva.exalushome/build/js/DataFrame";
import { Rck21ConfigParams, ReedSwitchOperationMode } from "./Rck21ConfigParams";
import { DependencyContainer } from "lavva.exalushome/build/js/DependencyContainer";
import { GetParamsErrorCode, SynchronizationTime } from "../../../ExtaLife";
import { IDevice } from "lavva.exalushome/build/js/Services/Devices/IDevice";
import { ResponseResult } from "lavva.exalushome/build/js/Services/FieldChangeResult";
import { GetParamsRequest } from "../Common/DataRequest";

export class Rck21ConfigService implements IRck21ConfigService {
    private _connection: IExalusConnectionService | null = null;
    public static readonly ServiceName: string = "Rck21ConfigService";

    constructor() {
        this._connection = Api.Get<IExalusConnectionService>(ExalusConnectionService.ServiceName);
    }

    GetServiceName(): string {
        return Rck21ConfigService.ServiceName;
    }

    async GetAllParamsAsync(device: IDevice, channel: number): Promise<Rck21ConfigParams | ResponseResult<GetParamsErrorCode>> {
        const result = await this._connection?.SendAndWaitForResponseAsync<Rck21ConfigParams>(new GetReedSwitchParamsRequest(device.Guid), 35000, false);

        if (result === null || result === undefined)
            return new ResponseResult(GetParamsErrorCode.UnknownError, `Failed to retrieve parameters - unknow error.`);

        switch (result.Status) {
            case Status.OK:
                if (result.Data != null) {
                    return result.Data;
                }
                break;
            case Status.ResourceIsNotAvailable:
                return new ResponseResult(GetParamsErrorCode.ResourceIsNotAvailable, `Failed to retrieve parameters - cannot communicate with the device.`);
        }

        return new ResponseResult(GetParamsErrorCode.OtherError, `Failed to retrieve parameters, response error: ${result.Status} data: ${result.Data}`);
    }

    async SetAllParamsAsync(params: Rck21ConfigParams, channel: number): Promise<Status> {
        const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetReedSwitchParamsRequest(params), 35000, false);

        if (result == null || result.Status == null) {
            DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
            return Status.FatalError;
        }

        if (result.Status !== Status.OK)
            DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

        return result.Status;
    }

    async SetReedSwitchOperationModeAsync(device: IDevice, channel: number, operationMode: ReedSwitchOperationMode): Promise<Status> {
        try {
            const currentConfig = await this.GetCurrentConfig(device.Guid);
            let params = new Rck21ConfigParams();

            if (currentConfig === null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - cannot read current configuration of device`);
                return Status.ResourceIsNotAvailable;
            }

            params.DeviceGuid = device.Guid;
            params.LogicNegation = currentConfig.LogicNegation;
            params.SyncTime = currentConfig.SyncTime;
            params.ReedSwitchMode = operationMode;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetReedSwitchParamsRequest(params), 35000, false);

            if (result == null || result.Status == null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

            return result.Status;
        }
        catch (error) {
            DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Cannot change device parameters. ${error}`);
            return Status.FatalError;
        }

    }

    async SetSynchronizationTimeAsync(device: IDevice, channel: number, syncTime: SynchronizationTime): Promise<Status> {
        try {
            const currentConfig = await this.GetCurrentConfig(device.Guid);
            let params = new Rck21ConfigParams();

            if (currentConfig === null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - cannot read current configuration of device`);
                return Status.ResourceIsNotAvailable;
            }

            params.DeviceGuid = device.Guid;
            params.LogicNegation = currentConfig.LogicNegation;
            params.SyncTime = syncTime;
            params.ReedSwitchMode = currentConfig.ReedSwitchMode;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetReedSwitchParamsRequest(params), 35000, false);

            if (result == null || result.Status == null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

            return result.Status;
        }
        catch (error) {
            DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Cannot change device parameters. ${error}`);
            return Status.FatalError;
        }
    }

    async SetLogicNegationAsync(device: IDevice, channel: number, logicNegation: boolean): Promise<Status> {
        try {
            const currentConfig = await this.GetCurrentConfig(device.Guid);
            let params = new Rck21ConfigParams();

            if (currentConfig === null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - cannot read current configuration of device`);
                return Status.ResourceIsNotAvailable;
            }

            params.DeviceGuid = device.Guid;
            params.LogicNegation = logicNegation;
            params.SyncTime = currentConfig.SyncTime;
            params.ReedSwitchMode = currentConfig.ReedSwitchMode;

            const result = await this._connection?.SendAndWaitForResponseAsync<DataFrame<null>>(new SetReedSwitchParamsRequest(params), 35000, false);

            if (result == null || result.Status == null) {
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters - unknow error.`);
                return Status.FatalError;
            }

            if (result.Status !== Status.OK)
                DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Failed to change device parameters, response status: ${result.Status}`);

            return result.Status;
            
        }
        catch (error) {
            DependencyContainer.Log?.Error(Rck21ConfigService.ServiceName, `Cannot change device parameters. ${error}`);
            return Status.FatalError;
        }
    }

    private async GetCurrentConfig(deviceGuid: string): Promise<Rck21ConfigParams | null> {
        const result = await this._connection?.SendAndWaitForResponseAsync<Rck21ConfigParams>(new GetReedSwitchParamsRequest(deviceGuid), 35000, false);

        if (result === null || result === undefined)
            return null;

        switch (result.Status) {
            case Status.OK:
                if (result.Data != null) {
                    return result.Data;
                }
                break;
            case Status.ResourceIsNotAvailable:
                return null;
        }

        throw new Error(`Failed to read current configuration of device! No data in response.`);
    }
}

class GetReedSwitchParamsRequest extends DataFrame<GetParamsRequest> {
    constructor(guid: string) {
        super();
        this.Resource = "/extalife/device/parameters/reedswitch";
        this.Method = Method.Get;
        this.Data = new GetParamsRequest(guid);
    }
}

class SetReedSwitchParamsRequest extends DataFrame<Rck21ConfigParams> {
    constructor(data: Rck21ConfigParams) {
        super();
        this.Resource = "/extalife/device/parameters/reedswitch";
        this.Method = Method.Put;
        this.Data = data;
    }
}